import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { usePortalAuth } from '../../context/PortalAuthContext';
import { inZone } from '../../utils/companyDate';
import { formatPriceOrPending, priceIsKnown, portalPriceText, pendingServicesNote } from '../../utils/formatCurrency';
import { formatPhone } from '../../utils/formatPhone';
import usePullToRefresh from '../../hooks/usePullToRefresh';

function CustomerInvoices() {
  const navigate = useNavigate();
  const { customer, logout, getAuthHeader, isAuthenticated, loading: authLoading } = usePortalAuth();

  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, unpaid, paid

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/portal/login');
    }
  }, [authLoading, isAuthenticated, navigate]);

  const loadInvoices = useCallback(async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/portal/invoices`,
        { headers: getAuthHeader() }
      );
      setInvoices(response.data);
    } catch (err) {
      console.error('Failed to load invoices:', err);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    if (isAuthenticated) {
      loadInvoices();
    }
  }, [isAuthenticated, loadInvoices]);

  const { PullToRefreshIndicator } = usePullToRefresh(loadInvoices);

  const handleLogout = () => {
    logout();
    navigate('/portal/login');
  };

  // completedAt is an instant; the day shown is the company's, not the phone's.
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', inZone(customer?.timezone, {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }));
  };

  const filteredInvoices = invoices.filter(inv => {
    if (filter === 'unpaid') return inv.paymentStatus !== 'paid' && !inv.isComp;
    if (filter === 'paid') return inv.paymentStatus === 'paid' || inv.isComp;
    return true;
  });

  // Same rule as the dashboard, and it has to BE the same rule: a customer seeing
  // two different balances on two pages of the same portal is the failure this
  // change exists to prevent. An unpriced invoice is not a zero, so it comes out
  // of the sum and is counted beside it.
  const allUnpaid = invoices.filter(i => i.paymentStatus !== 'paid' && !i.isComp);
  // priceIsKnown, not priceKnown: an invoice whose base is set but whose salt
  // price is not carries a total smaller than the truth, and adding that to the
  // balance understates it in exactly the way this change exists to stop.
  const pricedUnpaid = allUnpaid.filter(priceIsKnown);
  const pendingCount = allUnpaid.length - pricedUnpaid.length;
  const unpaidTotal = pricedUnpaid.reduce((sum, i) => sum + i.total, 0);
  const balanceKnown = !(pendingCount > 0 && pricedUnpaid.length === 0);
  const pendingNote = pendingServicesNote(allUnpaid.filter(i => !priceIsKnown(i)));

  if (authLoading) {
    return (
      <div className="portal-loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="portal-page">
      <PullToRefreshIndicator />
      <header className="portal-header">
        <div className="portal-header-content">
          <div className="portal-brand">
            <h1>{customer?.company?.name || 'Customer Portal'}</h1>
          </div>
          <nav className="portal-nav">
            <Link to="/portal" className="portal-nav-link">Dashboard</Link>
            <Link to="/portal/services" className="portal-nav-link">Services</Link>
            <Link to="/portal/invoices" className="portal-nav-link active">Invoices</Link>
            <button onClick={handleLogout} className="portal-nav-link logout">Logout</button>
          </nav>
        </div>
      </header>

      <main className="portal-main">
        <div className="portal-container">
          <div className="page-header">
            <div className="page-header-text">
              <h2>Invoices</h2>
              <p>View and pay your invoices</p>
            </div>
            {/* Was gated on unpaidTotal > 0, so a customer whose only unpaid service
                had no price saw no badge at all. Anything unpaid now shows here. */}
            {(unpaidTotal > 0 || pendingCount > 0) && (
              <div className="balance-badge">
                <span className="balance-label">Balance Due</span>
                <span className={`balance-amount${balanceKnown ? '' : ' price-pending'}`}>
                  {formatPriceOrPending(unpaidTotal, balanceKnown)}
                </span>
                {pendingCount > 0 && (
                  <span className="balance-pending">
                    {balanceKnown ? `plus ${pendingNote}` : pendingNote}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Filters */}
          <div className="filter-tabs">
            <button
              className={`filter-tab ${filter === 'all' ? 'active' : ''}`}
              onClick={() => setFilter('all')}
            >
              All ({invoices.length})
            </button>
            <button
              className={`filter-tab ${filter === 'unpaid' ? 'active' : ''}`}
              onClick={() => setFilter('unpaid')}
            >
              Unpaid ({invoices.filter(i => i.paymentStatus !== 'paid' && !i.isComp).length})
            </button>
            <button
              className={`filter-tab ${filter === 'paid' ? 'active' : ''}`}
              onClick={() => setFilter('paid')}
            >
              Paid ({invoices.filter(i => i.paymentStatus === 'paid' || i.isComp).length})
            </button>
          </div>

          {loading ? (
            <div className="portal-loading-inline">Loading invoices...</div>
          ) : filteredInvoices.length === 0 ? (
            <div className="portal-empty-state">
              <p>{filter === 'all' ? 'No invoices yet.' : `No ${filter} invoices.`}</p>
            </div>
          ) : (
            <div className="invoices-list">
              {filteredInvoices.map(invoice => (
                <div key={invoice.id} className="invoice-card">
                  <div className="invoice-info">
                    <div className="invoice-date">{formatDate(invoice.completedAt)}</div>
                    <div className="invoice-service">{invoice.baseLabel || 'Service'}</div>
                    {invoice.onCallPremiumPercent > 0 && invoice.pendingDepth !== true && (
                      <div className="invoice-service">On-call premium ({Number(invoice.onCallPremiumPercent)}%)</div>
                    )}
                  </div>
                  <div className="invoice-amount">
                    {invoice.isComp ? (
                      <span className="comp-label">COMP</span>
                    ) : (
                      <span className={`amount${priceIsKnown(invoice) ? '' : ' price-pending'}`}>
                        {portalPriceText(invoice, invoice.total)}
                      </span>
                    )}
                  </div>
                  <div className="invoice-status">
                    {invoice.isComp ? (
                      <span className="status-badge comp">Comp</span>
                    ) : invoice.paymentStatus === 'paid' ? (
                      <span className="status-badge paid">Paid</span>
                    ) : invoice.paymentStatus === 'invoice_sent' ? (
                      <span className="status-badge pending">Invoice Sent</span>
                    ) : (
                      <span className="status-badge unpaid">Unpaid</span>
                    )}
                  </div>
                  <div className="invoice-actions">
                    {/* priceKnown is belt and braces: an unpriced stop has never been
                        invoiced, so squareInvoiceUrl is already absent. */}
                    {invoice.squareInvoiceUrl && invoice.paymentStatus !== 'paid' && !invoice.isComp && priceIsKnown(invoice) && invoice.pendingDepth !== true && (
                      <a
                        href={invoice.squareInvoiceUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="pay-btn"
                      >
                        Pay Now
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Payment Info */}
          {customer?.company && (
            <div className="payment-info-section">
              <h3>Payment Questions?</h3>
              <p>Contact us if you have any questions about your invoices:</p>
              <div className="contact-options">
                {customer.company.phone && (
                  <a href={`tel:${customer.company.phone}`} className="contact-option">
                    📞 {formatPhone(customer.company.phone)}
                  </a>
                )}
                {customer.company.email && (
                  <a href={`mailto:${customer.company.email}`} className="contact-option">
                    ✉️ {customer.company.email}
                  </a>
                )}
              </div>
            </div>
          )}
        </div>
      </main>

      <style>{`
        .portal-page {
          min-height: 100vh;
          background: #f1f5f9;
        }
        .portal-loading {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #f1f5f9;
        }
        .portal-loading .spinner {
          width: 40px;
          height: 40px;
          border: 3px solid #e2e8f0;
          border-top-color: #3b82f6;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        .portal-header {
          background: linear-gradient(135deg, var(--color-primary, #1e88e5) 0%, var(--color-primary-dark, #1565c0) 100%);
          color: white;
          padding: 16px 20px;
          position: sticky;
          top: 0;
          z-index: 100;
        }
        .portal-header-content {
          max-width: 1200px;
          margin: 0 auto;
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 12px;
        }
        .portal-brand h1 {
          font-size: 1.25rem;
          font-weight: 600;
          margin: 0;
        }
        .portal-nav {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }
        .portal-nav-link {
          background: rgba(255,255,255,0.1);
          border: none;
          color: rgba(255,255,255,0.8);
          padding: 8px 16px;
          border-radius: 6px;
          text-decoration: none;
          font-size: 0.9rem;
          cursor: pointer;
          transition: all 0.15s;
        }
        .portal-nav-link:hover, .portal-nav-link.active {
          background: rgba(255,255,255,0.2);
          color: white;
        }
        .portal-nav-link.logout {
          background: rgba(239,68,68,0.2);
          color: #fca5a5;
        }
        .portal-nav-link.logout:hover {
          background: rgba(239,68,68,0.3);
          color: white;
        }
        .portal-main {
          padding: 24px 20px;
        }
        .portal-container {
          max-width: 800px;
          margin: 0 auto;
        }
        .page-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 24px;
          gap: 16px;
          flex-wrap: wrap;
        }
        .page-header-text h2 {
          font-size: 1.5rem;
          font-weight: 600;
          color: #0f172a;
          margin: 0 0 4px 0;
        }
        .page-header-text p {
          color: #64748b;
          margin: 0;
        }
        .balance-badge {
          background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
          color: white;
          padding: 12px 20px;
          border-radius: 12px;
          text-align: right;
        }
        .balance-label {
          display: block;
          font-size: 0.75rem;
          opacity: 0.9;
        }
        .balance-amount {
          display: block;
          font-size: 1.5rem;
          font-weight: 700;
        }
        /* Grey and italic so a short phrase is never read as a figure. */
        .price-pending {
          color: #64748b;
          font-style: italic;
          font-weight: 500;
        }
        /* On the red Balance Due badge grey would be illegible, so the phrase keeps
           the badge's white text and is distinguished by italic and size instead. */
        .balance-amount.price-pending {
          color: white;
          font-size: 1.05rem;
          font-weight: 600;
        }
        .balance-pending {
          display: block;
          font-size: 0.75rem;
          opacity: 0.9;
          margin-top: 3px;
          font-style: italic;
        }
        .amount.price-pending {
          font-size: 0.95rem;
        }
        .filter-tabs {
          display: flex;
          gap: 8px;
          margin-bottom: 16px;
          flex-wrap: wrap;
        }
        .filter-tab {
          background: white;
          border: 1px solid #e2e8f0;
          padding: 8px 16px;
          border-radius: 8px;
          font-size: 0.9rem;
          color: #64748b;
          cursor: pointer;
          transition: all 0.15s;
        }
        .filter-tab:hover {
          border-color: #cbd5e1;
        }
        .filter-tab.active {
          background: #0f172a;
          border-color: #0f172a;
          color: white;
        }
        .portal-loading-inline {
          text-align: center;
          padding: 40px;
          color: #64748b;
        }
        .portal-empty-state {
          background: white;
          padding: 40px;
          border-radius: 12px;
          text-align: center;
          color: #64748b;
        }
        .invoices-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .invoice-card {
          background: white;
          border-radius: 12px;
          padding: 16px 20px;
          display: flex;
          align-items: center;
          gap: 16px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }
        .invoice-info {
          flex: 1;
        }
        .invoice-date {
          font-size: 0.875rem;
          color: #64748b;
        }
        .invoice-service {
          font-weight: 500;
          color: #0f172a;
        }
        .invoice-amount {
          text-align: right;
          min-width: 80px;
        }
        .invoice-amount .amount {
          font-size: 1.25rem;
          font-weight: 700;
          color: #0f172a;
        }
        .invoice-amount .comp-label {
          background: #dbeafe;
          color: #1d4ed8;
          padding: 4px 10px;
          border-radius: 4px;
          font-size: 0.75rem;
          font-weight: 600;
        }
        .invoice-status {
          min-width: 100px;
          text-align: center;
        }
        .status-badge {
          display: inline-block;
          padding: 6px 12px;
          border-radius: 20px;
          font-size: 0.8rem;
          font-weight: 600;
        }
        .status-badge.paid, .status-badge.comp {
          background: #dcfce7;
          color: #15803d;
        }
        .status-badge.pending {
          background: #dbeafe;
          color: #1d4ed8;
        }
        .status-badge.unpaid {
          background: #fef3c7;
          color: #92400e;
        }
        .invoice-actions {
          min-width: 100px;
          text-align: right;
        }
        .pay-btn {
          display: inline-block;
          background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
          color: white;
          padding: 8px 20px;
          border-radius: 8px;
          text-decoration: none;
          font-weight: 600;
          font-size: 0.9rem;
          transition: all 0.15s;
        }
        .pay-btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(34, 197, 94, 0.3);
        }
        .payment-info-section {
          background: white;
          border-radius: 12px;
          padding: 24px;
          margin-top: 32px;
          text-align: center;
        }
        .payment-info-section h3 {
          font-size: 1.1rem;
          font-weight: 600;
          color: #0f172a;
          margin: 0 0 8px 0;
        }
        .payment-info-section p {
          color: #64748b;
          margin: 0 0 16px 0;
        }
        .contact-options {
          display: flex;
          justify-content: center;
          gap: 24px;
          flex-wrap: wrap;
        }
        .contact-option {
          color: #3b82f6;
          text-decoration: none;
          font-size: 0.95rem;
        }
        .contact-option:hover {
          text-decoration: underline;
        }

        @media (max-width: 640px) {
          .portal-header-content {
            flex-direction: column;
            align-items: flex-start;
          }
          .page-header {
            flex-direction: column;
          }
          .balance-badge {
            text-align: left;
          }
          .invoice-card {
            flex-direction: column;
            align-items: flex-start;
            gap: 12px;
          }
          .invoice-amount, .invoice-status, .invoice-actions {
            width: 100%;
            text-align: left;
          }
          .invoice-status {
            order: -1;
          }
        }
      `}</style>
    </div>
  );
}

export default CustomerInvoices;
